import React, { useState } from "react";
import {
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  IconButton,
} from "@mui/material";
import Unchecked from "../../../svg/unchecked.svg?react";
import XIcon from "../../../svg/x.svg?react";
import Checked from "../../../svg/checked.svg?react";
import styles from "../QuoteMultipleDestinationsPanel.module.css";
import { IOccupant } from "../../../types/ILocation";
import { useIsMobile } from "../../../hooks/useIsMobile";

const AddProductPanelContent: React.FC<{
  occupants: IOccupant[];
  pinType: "koch" | "customer" | "competitor" | "kochAndCompetitor";
  close: () => void;
  submit: (selected: IOccupant[]) => Promise<void>;
}> = ({ occupants, pinType, close, submit }) => {
  const isMobile = useIsMobile();

  const [selectedOccupants, setSelectedOccupants] = useState<IOccupant[]>([]);

  // select logic
  const handleToggle = (occupant: IOccupant) => {
    const currentIndex = selectedOccupants.findIndex(
      (o) => o.id === occupant.id,
    );
    const isSelected = currentIndex >= 0;
    const newSelected = [...selectedOccupants];

    if (isSelected) {
      newSelected.splice(currentIndex, 1);
    } else {
      newSelected.push(occupant);
    }

    setSelectedOccupants(newSelected);
  };

  return (
    <>
      {isMobile && (
        <div
          style={{
            position: "fixed",
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            zIndex: 10199,
            background: "rgba(0, 0, 0, 0.5)",
          }}
        />
      )}
      <div className={styles.addProductToDestinationPanel}>
        <div className={styles.addProductToDestinationPanelContainer}>
          {/* TITLE BAR */}
          <div
            style={{
              display: "flex",
            }}
          >
            {/* BACK BUTTON */}
            <div className={styles.addProductToDestinationCloseButton}>
              <IconButton onClick={close}>
                <XIcon className={styles.addProductToDestinationCloseIcon} />
              </IconButton>
            </div>
            {/* HEADER TEXT */}
            <div className={styles.addProductToDestinationTitle}>
              Which Occupants Buy This Product?
            </div>
          </div>

          {/* SUBTITLE */}
          <div className={styles.addProductSubtitle}>
            Select one or more occupants at this location
          </div>

          {/* OCCUPANT OPTIONS */}
          <div className={styles.addProductOptionMetaContainer}>
            <FormGroup sx={{ gap: "7px" }}>
              {occupants.map((occupant) => {
                const occupantType =
                  occupant.types.length > 1 ? "Competitor" : occupant.types[0];
                let disabled: boolean;
                // "customer" enables all occupants regardless of type;
                // "kochAndCompetitor" enables Koch and Competitor occupants;
                // any other type enables only occupants whose type matches exactly.
                if (pinType === "customer") {
                  disabled = false;
                } else if (pinType === "kochAndCompetitor") {
                  disabled =
                    occupantType !== "Koch" && occupantType !== "Competitor";
                } else {
                  disabled = occupantType.toLowerCase() !== pinType;
                }
                return (
                  <div
                    key={occupant.id}
                    className={styles.addProductOptionContainer}
                    style={disabled ? { opacity: 0.4 } : undefined}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          disabled={disabled}
                          icon={
                            <Unchecked
                              style={{ paddingLeft: 4, paddingRight: 3 }}
                            />
                          }
                          onChange={() => handleToggle(occupant)}
                          checkedIcon={<Checked fontSize="23px" />}
                        />
                      }
                      label={occupant.name}
                    />
                  </div>
                );
              })}
            </FormGroup>
          </div>

          {/* BUTTONS */}
          <div className={styles.addProductButtonContainer}>
            <Button
              sx={{
                borderRadius: "8.5px",
                border: "1.25px solid rgba(0, 0, 0, 0.08)",
                color: "#0a0a0a",
                backgroundColor: "#fff",
                fontFamily: "inter",
                fontWeight: 400,
                fontSize: "12.25px",
                padding: "7px 14px",
              }}
              onClick={close}
            >
              Cancel
            </Button>
            <Button
              sx={{
                borderRadius: "8.5px",
                background: "#155DFC",
                color: "#FFFFFF",
                fontFamily: "inter",
                fontWeight: 400,
                fontSize: "12.25px",
                padding: "7px 14px",
                "&:hover": {
                  backgroundColor: "secondary.main",
                },
                "&:disabled": {
                  backgroundColor: "rgba(21, 93, 252, 0.5)",
                  color: "rgba(255, 255, 255, 0.7)",
                },
              }}
              disabled={selectedOccupants.length === 0}
              onClick={async () => {
                await submit(selectedOccupants);
              }}
            >
              Done
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddProductPanelContent;
